import { useState, useEffect, useRef, useCallback } from "react";

interface Props {
  activity?: any | null;
  onClose: () => void;
  onSave: (data: any) => void;
}

export default function ActivityFormModal({
  activity,
  onClose,
  onSave,
}: Props) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    activity_type_id: 1,
    date: "",
    time: "",
    location: "",
    status: "active" as string,
    image: "",
    max_participants: 30,
  });
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (activity) {
      setForm({
        title: activity.title,
        description: activity.description || "",
        activity_type_id: activity.activity_type_id || 1,
        date: activity.date,
        time: activity.time,
        location: activity.location,
        status: activity.status,
        image: activity.image || "",
        max_participants: activity.max_participants || 30,
      });
    }
  }, [activity]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const processFile = useCallback((file: File) => {
    if (!file.type.match(/image\/(jpeg|png|webp)/)) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm((prev) => ({ ...prev, image: ev.target?.result as string }));
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  const inputClass =
    "w-full px-4 py-2.5 text-sm rounded-xl border-2 border-rose-100 bg-rose-50/30 focus:outline-none focus:border-rose-300 transition-all";
  const labelClass = "block text-xs font-semibold text-gray-600 mb-1.5";

  return (
    <div
      className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center p-4"
      dir="rtl"
    >
      <div className="bg-white rounded-2xl border-2 border-rose-100 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-rose-100 sticky top-0 bg-white z-10">
          <h2 className="font-bold text-gray-800 text-base">
            {activity ? "עריכת פעילות" : "הוספת פעילות חדשה"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-400 hover:bg-gray-50 cursor-pointer transition-all"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className={labelClass}>תמונה</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
            {!form.image ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                className={`w-full h-36 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-all ${dragOver ? "border-rose-400 bg-rose-50" : "border-rose-200 bg-rose-50/40 hover:bg-rose-50 hover:border-rose-300"}`}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-rose-100 text-rose-400">
                  <i className="ri-upload-cloud-2-line text-xl"></i>
                </div>
                <div className="text-center">
                  <p className="text-xs font-semibold text-gray-600">
                    גרור תמונה או לחץ להעלאה
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, WebP</p>
                </div>
              </div>
            ) : (
              <div className="relative w-full h-36 rounded-xl overflow-hidden border-2 border-rose-100 group">
                <img
                  src={form.image}
                  alt="preview"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white text-gray-700 text-xs font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line text-sm"></i> החלף
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setForm((f) => ({ ...f, image: "" }));
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line text-sm"></i> הסר
                  </button>
                </div>
              </div>
            )}
          </div>
          <div>
            <label className={labelClass}>כותרת *</label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              required
              placeholder="שם הפעילות..."
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>תיאור</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              placeholder="תיאור קצר של הפעילות..."
              className={`${inputClass} resize-none`}
              maxLength={500}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>סוג פעילות</label>
              <select
                name="activity_type_id"
                value={form.activity_type_id}
                onChange={handleChange}
                className={inputClass}
              >
                <option value={1}>וובינר</option>
                <option value={2}>סדנה</option>
                <option value={3}>הכשרה</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>סטטוס</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="active">פעיל</option>
                <option value="ended">הסתיים</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>תאריך</label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>שעה</label>
              <input
                type="time"
                name="time"
                value={form.time}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>מיקום</label>
              <input
                type="text"
                name="location"
                value={form.location}
                onChange={handleChange}
                placeholder="עיר / כתובת / אונליין..."
                className={inputClass}
              />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              className="flex-1 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap"
            >
              {activity ? "שמור שינויים" : "הוסף פעילות"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
